import { Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { filter, map, mergeMap, tap } from 'rxjs/operators'
import { CallerService } from 'src/app/services/caller.service'
import { SocketIoService } from 'src/app/services/socket-io.service'
import { SocketIoChatNamespaceService } from 'src/app/services/socket-io.chat-ns.service'
import { IOEventName } from 'src/app/utils/types'
import { deletePostedPost, messagesRead, newAccommodationData, newChatMessage, newSaleData, saleSoldChanged, setCorpMemberProfileData } from '../actions/corp-member.actions'

/** POST_REMOVED's payload. */
type PostRemoved = { type: 'sale' | 'accommodation', id: number }
/** SALE_SOLD_CHANGED's payload. */
type SaleSoldChanged = { id: number, sold_at?: string | null }

function isPostRemoved(data: unknown): data is PostRemoved {
  const event = data as Partial<PostRemoved> | null
  return (event?.type === 'sale' || event?.type === 'accommodation') && Number.isInteger(event?.id)
}

function isSaleSoldChanged(data: unknown): data is SaleSoldChanged {
  return Number.isInteger((data as Partial<SaleSoldChanged> | null)?.id)
}

/** MESSAGES_READ's payload. */
type MessagesRead = { room: string, reader: string, read_at: string }

function isMessagesRead(data: unknown): data is MessagesRead {
  const event = data as Partial<MessagesRead> | null
  return typeof event?.room === 'string' && typeof event?.reader === 'string' && typeof event?.read_at === 'string'
}

/**
 * Owns every "socket event -> dispatch an NgRx action" mapping in the app. Registered once at
 * bootstrap (see app.module.ts's EffectsModule.forRoot) and lives for the whole session — unlike
 * a component-level socket listener, this can never go missing just because the user navigated
 * to a different page. This is the fix for chat messages / broadcasts only updating the store
 * while a specific page (Messages, previously) happened to be mounted.
 */
@Injectable()
export class SocketEffects {
  // A single broadcast can carry sales and/or accommodations — mergeMap dispatches one action
  // per kind present, so both a shared subscription and full behavior parity with having two
  // separate effects are kept (neither update is ever dropped just because the other is absent).
  newPostsFromBroadcast$ = createEffect(() =>
    this.socketIoService.onEvent(IOEventName.BROADCAST_MESSAGE).pipe(
      mergeMap((data: any) => {
        const actions: Action[] = []
        if (data?.posts?.sales) actions.push(newSaleData({ data: data.posts.sales }))
        if (data?.posts?.accommodations) actions.push(newAccommodationData({ data: data.posts.accommodations }))
        return actions
      }),
    ),
  )

  // Open feeds drop a post as soon as it's deleted, and show a sale as sold - not only after a reload.
  postRemoved$ = createEffect(() =>
    this.socketIoService.onEvent(IOEventName.POST_REMOVED).pipe(
      filter(isPostRemoved),
      map((data) => deletePostedPost({ id: `${data.type}_${data.id}` })),
    ),
  )

  saleSoldChanged$ = createEffect(() =>
    this.socketIoService.onEvent(IOEventName.SALE_SOLD_CHANGED).pipe(
      filter(isSaleSoldChanged),
      map((data) => saleSoldChanged({ id: data.id, sold_at: data.sold_at ?? null })),
    ),
  )

  chatMessage$ = createEffect(() =>
    this.socketIoChatNamespaceService.onEvent(IOEventName.CHAT_MESSAGE).pipe(
      map((data: any) => newChatMessage(data)),
    ),
  )

  // The other person read our messages: ✓ -> ✓✓, live.
  messagesRead$ = createEffect(() =>
    this.socketIoChatNamespaceService.onEvent(IOEventName.MESSAGES_READ).pipe(
      filter(isMessagesRead),
      map(({ room, reader, read_at }) => messagesRead({ room, reader, read_at })),
    ),
  )

  // The single, structural place sockets get (re)connected after authentication — every
  // login/signup flow (email + Google, x2 each) and the app-boot "already logged in" path all
  // dispatch setCorpMemberProfileData, so reacting to it here means no call site can forget to
  // connect the sockets (previously this was 5 manual `connectSockets()` calls sprinkled across
  // app.component.ts/login.component.ts/signup.component.ts, one of which was actually called
  // *before* the dispatch — see git history — leaving the socket connected under a stale identity
  // until the next full reload). connect() is idempotent, so this is also always safe to re-run.
  connectSocketsOnProfileSet$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setCorpMemberProfileData),
        tap(() => this.callerService.connectSockets()),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private callerService: CallerService,
    private socketIoService: SocketIoService,
    private socketIoChatNamespaceService: SocketIoChatNamespaceService,
  ) {}
}
